import React, {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

import type { IgAudioTrack } from "../../../../../api/post";
import { cn } from "../../../../../lib/utils";
import { ScrollSentinel } from "../../../../common/ScrollSentinel";
import { Button } from "../../../../ui/button";
import { Spinner } from "../../../../ui/spinner";
import { trackAttribution, trackDuration, trackTitle } from "./audio-catalog";
import { AudioPreviewButton, HearOnInstagramLink } from "./AudioIconAction";

interface AudioTrackListProps {
  tracks: IgAudioTrack[];
  busy: boolean;
  hasMore: boolean;
  autoPaused?: boolean;
  exhausted?: boolean;
  searched?: boolean;
  resetKey?: number;
  selectedAudioId?: string;
  previewingAudioId?: string;
  disabled?: boolean;
  loadMoreError?: string;

  onSelect(track: IgAudioTrack): void;

  onPreview(track: IgAudioTrack): void;

  onPreviewEnded(): void;

  onLoadMore(): void;
}

export const TrackArtwork: React.FC<{
  track?: IgAudioTrack;
  className?: string;
}> = ({ track, className }) => {
  const src = track?.coverArtworkThumbnailUri || track?.profilePictureUrl;
  const [failedSrc, setFailedSrc] = useState<string>();

  if (!src || failedSrc === src) {
    return (
      <span
        className={cn(
          "flex size-9 shrink-0 items-center justify-center rounded-md bg-neutral-100 text-xs text-neutral-500",
          className
        )}
        data-testid="ig-audio-artwork-placeholder"
        aria-hidden="true"
      >
        <i className="fa-regular fa-music" />
      </span>
    );
  }

  return (
    <img
      className={cn("size-9 shrink-0 rounded-md object-cover", className)}
      src={src}
      alt=""
      onError={() => setFailedSrc(src)}
    />
  );
};

const rowFor = (box: HTMLElement, audioId: string | undefined) =>
  audioId
    ? [...box.querySelectorAll<HTMLElement>("[data-audio-row]")].find(
        (row) => row.dataset.audioRow === audioId
      )
    : undefined;

/**
 * The rows of the audio picker.
 *
 * Preview plays the TRACK through one hidden `<audio>` element, so starting a
 * track stops whichever one was playing. It is the only preview that exists:
 * Meta does not support previewing a reel with audio attached.
 */
export const AudioTrackList: React.FC<AudioTrackListProps> = ({
  tracks,
  busy,
  hasMore,
  autoPaused,
  exhausted,
  searched,
  resetKey,
  selectedAudioId,
  previewingAudioId,
  disabled,
  loadMoreError,
  onSelect,
  onPreview,
  onPreviewEnded,
  onLoadMore,
}) => {
  const listId = useId();
  const listed = tracks.filter((track) => Boolean(track.audioId));
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const [moreBelow, setMoreBelow] = useState(false);
  const firstNewIndexRef = useRef<number | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const endedRef = useRef(onPreviewEnded);
  endedRef.current = onPreviewEnded;
  const previewingIdRef = useRef(previewingAudioId);
  previewingIdRef.current = previewingAudioId;
  const [playingId, setPlayingId] = useState<string>();
  const [previewErrorId, setPreviewErrorId] = useState<string>();
  const previewUrl = tracks.find(
    (track) => track.audioId === previewingAudioId
  )?.downloadUrl;

  useEffect(() => {
    setPreviewErrorId(undefined);
  }, [tracks]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    if (!previewUrl) {
      audio.removeAttribute("src");
      return;
    }

    let cancelled = false;
    const audioId = previewingIdRef.current;
    setPlayingId(undefined);
    setPreviewErrorId(undefined);
    audio.src = previewUrl;
    const playing = audio.play();
    playing?.catch?.((error: unknown) => {
      if (cancelled || (error as Error | undefined)?.name === "AbortError")
        return;
      if (audioId === previewingIdRef.current) failPreview();
    });
    return () => {
      cancelled = true;
      audio.pause();
    };
  }, [previewUrl]);

  const failPreview = () => {
    const audioId = previewingIdRef.current;
    if (!audioId) return;
    setPreviewErrorId(audioId);
    endedRef.current();
  };

  const measureMoreBelow = useCallback(() => {
    const box = scrollerRef.current;
    if (!box) return;
    setMoreBelow(box.scrollTop + box.clientHeight < box.scrollHeight - 1);
  }, []);

  useLayoutEffect(() => {
    measureMoreBelow();
  }, [measureMoreBelow, listed.length, busy, previewErrorId]);

  useEffect(() => {
    const box = scrollerRef.current;
    if (!box || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measureMoreBelow);
    observer.observe(box);
    return () => observer.disconnect();
  }, [measureMoreBelow]);

  useLayoutEffect(() => {
    firstNewIndexRef.current = null;
    if (scrollerRef.current) scrollerRef.current.scrollTop = 0;
  }, [resetKey]);

  useLayoutEffect(() => {
    const from = firstNewIndexRef.current;
    if (from === null || busy) return;
    firstNewIndexRef.current = null;
    const box = scrollerRef.current;
    if (!box) return;
    const firstNew = rowFor(box, listed[from]?.audioId);
    if (firstNew) box.scrollTop = firstNew.offsetTop;
    if (!hasMore && document.activeElement === document.body) {
      (firstNew ?? rowFor(box, listed.at(-1)?.audioId))
        ?.querySelector("button")
        ?.focus();
    }
  });

  useEffect(() => {
    const box = scrollerRef.current;
    const row = box && rowFor(box, previewingAudioId);
    if (!box || !row || typeof IntersectionObserver === "undefined") return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) endedRef.current();
      },
      { root: box }
    );
    observer.observe(row);
    return () => observer.disconnect();
  }, [previewingAudioId]);

  const showMore = () => {
    if (busy) return;
    firstNewIndexRef.current = listed.length;
    onLoadMore();
  };

  const count = `${listed.length} ${listed.length === 1 ? "track" : "tracks"}${
    hasMore ? " so far" : ""
  }`;

  return (
    <div data-testid="ig-audio-track-list">
      <audio
        ref={audioRef}
        hidden
        preload="none"
        data-testid="ig-audio-preview-player"
        onEnded={onPreviewEnded}
        onPlaying={() => setPlayingId(previewingIdRef.current)}
        onWaiting={() => setPlayingId(undefined)}
        onError={failPreview}
      >
        <track kind="captions" />
      </audio>

      <div
        ref={scrollerRef}
        className={cn(
          "relative -mx-2 flex max-h-[min(456px,60vh)] flex-col gap-1 overflow-y-auto",
          moreBelow &&
            "[mask-image:linear-gradient(to_bottom,black_calc(100%_-_2rem),transparent)]"
        )}
        data-testid="ig-audio-track-scroller"
        onScroll={measureMoreBelow}
      >
        {busy && listed.length === 0 && (
          <div className="m-0 flex items-center gap-2 px-2 py-3 font-inter text-xs text-neutral-600">
            <Spinner size="sm" className="motion-reduce:animate-none" />
            Searching Instagram audio...
          </div>
        )}

        {!busy && listed.length === 0 && (
          <p
            className="m-0 px-2 py-3 font-inter text-xs text-neutral-600"
            data-testid="ig-audio-empty"
          >
            No tracks matched that search.
          </p>
        )}

        {listed.map((track) => {
          const selected = Boolean(
            track.audioId && track.audioId === selectedAudioId
          );
          const title = trackTitle(track);
          const detail = [trackAttribution(track), trackDuration(track)]
            .filter(Boolean)
            .join(" · ");
          const detailId = `${listId}-${track.audioId}-detail`;

          return (
            <div
              key={track.audioId}
              className="flex flex-col"
              data-audio-row={track.audioId}
            >
              <div
                className={cn(
                  "relative flex items-center gap-3 rounded-lg border border-solid px-2 py-1.5 transition-colors motion-reduce:transition-none",
                  selected
                    ? "border-blue-200 bg-blue-50"
                    : "border-transparent bg-white",
                  !selected && !disabled && "hover:bg-neutral-50"
                )}
                data-testid={`ig-audio-track-${track.audioId}`}
                data-selected={selected || undefined}
              >
                <TrackArtwork track={track} />

                <button
                  type="button"
                  disabled={disabled}
                  aria-label={`Use ${title}`}
                  aria-pressed={selected}
                  aria-describedby={detail ? detailId : undefined}
                  className={cn(
                    "flex min-w-0 grow flex-col items-start border-0 bg-transparent p-0 text-left after:absolute after:inset-0 after:rounded-lg after:content-[''] focus-visible:outline-none focus-visible:after:ring-2 focus-visible:after:ring-blue-500",
                    disabled ? "cursor-not-allowed" : "cursor-pointer"
                  )}
                  onClick={() => onSelect(track)}
                >
                  <span className="w-full truncate font-inter text-[13px] font-medium leading-5 text-neutral-800">
                    {title}
                  </span>
                  {detail ? (
                    <span
                      id={detailId}
                      className="w-full truncate font-inter text-xs leading-4 text-neutral-600"
                    >
                      {detail}
                    </span>
                  ) : null}
                </button>

                <span className="flex w-8 shrink-0 justify-center">
                  {track.downloadUrl ? (
                    <AudioPreviewButton
                      title={title}
                      playing={previewingAudioId === track.audioId}
                      buffering={
                        previewingAudioId === track.audioId &&
                        playingId !== track.audioId
                      }
                      disabled={disabled}
                      onToggle={() => onPreview(track)}
                    />
                  ) : track.onPlatformAudioPreviewLink ? (
                    <HearOnInstagramLink
                      title={title}
                      href={track.onPlatformAudioPreviewLink}
                    />
                  ) : null}
                </span>
              </div>
              <div role="status" aria-live="polite">
                {previewErrorId === track.audioId ? (
                  <p className="m-0 flex items-center gap-1.5 px-2 pt-0.5 font-inter text-xs leading-4 text-neutral-700">
                    <i
                      className="fa-solid fa-triangle-exclamation text-yellow-600"
                      aria-hidden="true"
                    />
                    This preview could not play. Try another track.
                  </p>
                ) : null}
              </div>
            </div>
          );
        })}

        {hasMore ? (
          <ScrollSentinel
            hasMore={!autoPaused && !loadMoreError}
            isLoading={busy}
            onIntersect={onLoadMore}
          >
            <div className="flex h-10 items-center justify-center">
              {busy ? (
                <Spinner
                  size="sm"
                  aria-hidden="true"
                  className="motion-reduce:animate-none"
                />
              ) : null}
            </div>
          </ScrollSentinel>
        ) : exhausted && listed.length > 0 ? (
          <p className="m-0 px-2 py-3 font-inter text-xs leading-4 text-neutral-600">
            {searched
              ? "That's every track available for this search."
              : "That's every track available right now."}
          </p>
        ) : null}
      </div>

      <div role="status" aria-live="polite">
        {hasMore && loadMoreError ? (
          <p className="m-0 mt-1 font-inter text-xs leading-4 text-neutral-600">
            {loadMoreError}
          </p>
        ) : null}
      </div>

      <div
        className={cn(
          "flex items-center justify-between gap-2",
          (listed.length > 0 || hasMore) && "mt-1 min-h-7"
        )}
      >
        <p
          role="status"
          aria-live="polite"
          className="m-0 font-inter text-xs leading-4 text-neutral-600"
        >
          {listed.length > 0 ? count : null}
        </p>

        {hasMore && (
          <Button
            type="button"
            variant="black-tertiary"
            size="xs"
            className="aria-disabled:cursor-default aria-disabled:opacity-50 aria-disabled:hover:bg-transparent aria-disabled:active:scale-100 aria-disabled:active:bg-transparent"
            aria-disabled={busy || undefined}
            onClick={showMore}
          >
            {busy
              ? "Loading..."
              : loadMoreError
                ? "Try again"
                : "Show more tracks"}
          </Button>
        )}
      </div>
    </div>
  );
};

export default AudioTrackList;
